import * as Styles from './styles';

import { useContext, useEffect, useState } from 'react';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { MPosContext } from 'utils/context';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { isValidIMEI } from './utils';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import {
    SegmentPages,
    SegmentTrackEvents,
    SegmentEventNameTypes,
    SegmentEventButtonNames,
    SegmentEventActionTypes,
    SegmentEventActionNames,
    PRODUCT,
    SUB_PRODUCT_NAME,
    SegmentEventFieldTypes,
} from 'utils/segment/constants';
import { verifyDevice } from './api';
import ModelDetails from 'components/modelDetails';
import SnackBar from 'library/snackbar';
import { IMEIDetailsResponse, IMEIModeldetails } from './types';
import { NewPlanStages } from '../constants';
import { ImeiScannerModal } from 'features/imeiScanner';
import { Permissions } from 'utils/constants';

const IMEIModelScreen = () => {
    const {
        partnerId,
        dealerType,
        setPlanDetails,
        planDetails,
        setActiveStage,
        permissions,
        permissionsMap,
    } = useContext(MPosContext);

    const { isMobile } = useResponsiveDevice();

    const [onClickSerialNumber, setOnClickSerialNumber] = useState(false);
    const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
        useState(true);
    const [imei, setImei] = useState('');
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [buttonLoaders, setButtonLoaders] = useState({
        imeiVerifyLoader: false,
        submitButtonLoaders: false,
    });
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    const [isSerialNumberVerified, setIsSerialNumberVerified] = useState(false);
    const [modelDetails, setModelDetails] = useState<IMEIModeldetails>({
        id: '',
        activationDate: '',
        description: '',
        modelMappingName: '',
        name: '',
        price: 0,
        type: '',
        brand: '',
        showActivationDate: true,
    });

    const { group } = permissions;

    useEffect(() => {
        if (isValidIMEI(imei, partnerId)) {
            setIsContinueButtonDisabled(false);
        } else {
            setIsContinueButtonDisabled(true);
        }
    }, [imei]);

    useEffect(() => {
        if (onClickSerialNumber) {
            trackEvent(SegmentEventNameTypes.ACTION, {
                page_url: window?.location?.href || '',
                button_name: SegmentEventButtonNames.CLICK_SERIAL_NUMBER,
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.CLICK_SERIAL_NUMBER,
                product: PRODUCT,
                sub_product: SUB_PRODUCT_NAME,
                platform: isMobile ? 'mweb' : 'web',
                device: navigator.userAgent || '',
                partner: partnerId,
                field_selected: SegmentEventFieldTypes.TEXT,
                plan_id: planDetails.plan.plan_id,
                device_model: planDetails.device.name,
                category: planDetails.device.type,
            });
        }
    }, [onClickSerialNumber]);

    const [validationErr, setValidationErr] = useState({
        serial_number: '',
    });

    const validateIMEIHandler = async () => {
        setButtonLoaders((buttonLoaders) => ({
            ...buttonLoaders,
            imeiVerifyLoader: true,
        }));
        const imeiNumber = imei.trim();
        setImei(imei);

        try {
            const response: IMEIDetailsResponse = await verifyDevice(
                partnerId,
                imeiNumber,
                group
            );

            if (response.isValid) {
                setIsSerialNumberVerified(true);
                if (response.modelDetails) {
                    setModelDetails(response.modelDetails);
                }
                setSnackbar({
                    type: 'success',
                    message: response.message,
                });
            } else {
                setIsSerialNumberVerified(false);
                setSnackbar({
                    type: 'failure',
                    message: response.message,
                });
                setValidationErr((validationErr) => ({
                    ...validationErr,
                    serial_number: response.message,
                }));
            }
        } finally {
            setButtonLoaders((buttonLoaders) => ({
                ...buttonLoaders,
                imeiVerifyLoader: false,
            }));
        }
    };

    const onSubmit = async () => {
        setButtonLoaders((buttonLoaders) => ({
            ...buttonLoaders,
            submitButtonLoaders: true,
        }));
        setPlanDetails({
            ...planDetails,
            device: {
                ...planDetails.device,
                name: modelDetails.name,
                id: modelDetails.id,
                type: modelDetails.type,
                serial_number: imei.trim(),
                price: modelDetails.price,
                model_mapping_name: modelDetails.modelMappingName,
                brand: modelDetails.brand,
                is_device_verified_in_model_page: isSerialNumberVerified,
                activation_date: modelDetails.activationDate,
                show_activation_date: modelDetails.showActivationDate,
            },
        });
        try {
            await trackEvent(
                SegmentTrackEvents.TAP_SUBMIT_SERIAL_NUMBER_MODEL_PAGE,
                {
                    page: SegmentPages.NEW_PLAN_DEVICE_MODELS,
                    partner: partnerId,
                    product: 'pos',
                    journey: 'buy',
                    platform: 'mweb',
                    type: dealerType,
                }
            );
        } catch (e) {
            console.error(e);
        } finally {
            setButtonLoaders((buttonLoaders) => ({
                ...buttonLoaders,
                submitButtonLoaders: false,
            }));
            setActiveStage(NewPlanStages.PLAN);
        }
    };

    const onChangeIMEIHandler = (e) => {
        setImei(e.target.value);
        setValidationErr((validationErr) => ({
            ...validationErr,
            serial_number: '',
        }));
    };

    const [cameraSupported, setCameraSupported] = useState(false);

    useEffect(() => {
        setCameraSupported(
            typeof navigator !== 'undefined' &&
                !!navigator.mediaDevices?.getUserMedia
        );
    }, []);

    const handleImeiScanned = (scannedImei: string) => {
        setImei(scannedImei);
        setValidationErr((validationErr) => ({
            ...validationErr,
            serial_number: '',
        }));
        setIsScannerOpen(false);
    };

    return (
        <>
            <Styles.Wrapper>
                <Styles.EnterSerialNumberText>
                    Device Validation
                </Styles.EnterSerialNumberText>
                <Styles.SerialNumberText>
                    Enter device IMEI/Serial number to verify the details of the
                    device
                </Styles.SerialNumberText>
                <Styles.InputWrapper
                    onClick={() => setOnClickSerialNumber(true)}
                >
                    <Styles.EnterMoreDetails>
                        Device IMEI/Serial number
                    </Styles.EnterMoreDetails>
                    <Styles.InputAndScanWrapper>
                        <Styles.InputContainer>
                            <Input
                                className={''}
                                name={'Device Serial Number'}
                                placeHolder={'Enter IMEI/Serial number'}
                                type={'text'}
                                value={imei}
                                onChange={onChangeIMEIHandler}
                                errMsg={validationErr.serial_number}
                                hasError={
                                    validationErr?.serial_number?.length > 0
                                }
                            />
                        </Styles.InputContainer>
                        {permissionsMap[Permissions.SCANNER] &&
                            cameraSupported && (
                                <Styles.ScanButton
                                    type="button"
                                    onClick={() => setIsScannerOpen(true)}
                                    title="Scan IMEI barcode"
                                >
                                    📷 Scan
                                </Styles.ScanButton>
                            )}
                    </Styles.InputAndScanWrapper>
                </Styles.InputWrapper>
                <Styles.ButtonWrapper>
                    <PrimaryButton
                        disabled={isContinueButtonDisabled}
                        onClick={validateIMEIHandler}
                        loading={buttonLoaders.imeiVerifyLoader}
                    >
                        Validate device
                    </PrimaryButton>
                </Styles.ButtonWrapper>
                {modelDetails && isSerialNumberVerified && (
                    <>
                        <ModelDetails
                            activationDate={modelDetails.activationDate}
                            imei={imei}
                            modelName={modelDetails.name}
                            showActivationDate={modelDetails.showActivationDate}
                        />
                        <Styles.ButtonWrapper>
                            <PrimaryButton
                                disabled={isContinueButtonDisabled}
                                onClick={onSubmit}
                                loading={buttonLoaders.submitButtonLoaders}
                            >
                                Continue to plan selection
                            </PrimaryButton>
                        </Styles.ButtonWrapper>
                    </>
                )}
            </Styles.Wrapper>
            {permissionsMap[Permissions.SCANNER] && (
                <ImeiScannerModal
                    isOpen={isScannerOpen}
                    onClose={() => setIsScannerOpen(false)}
                    onImeiScanned={handleImeiScanned}
                />
            )}
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

export default IMEIModelScreen;
